import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { handleRedirectResult, loadEntraConfig, isInIframe } from "@/lib/entra";
import { toast } from "sonner";

export default function EntraCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [msg, setMsg] = useState("Verifying Microsoft sign-in…");
  const [err, setErr] = useState(null); // when set, we STOP auto-redirect and show detail

  // If we're inside MSAL's hidden silent-SSO iframe, do NOTHING.
  // MSAL processes the response internally via postMessage; rendering our React
  // app inside the iframe just wastes cycles and can trigger extra API calls.
  const inIframe = isInIframe();

  useEffect(() => {
    if (inIframe) return;
    (async () => {
      // Surface any error Microsoft sent back in the URL hash / query.
      const hash = window.location.hash || "";
      const hashErr = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const qErr = params.get("error");
      const errCode = qErr || hashErr.get("error");
      const errDesc = params.get("error_description") || hashErr.get("error_description");
      if (errCode) {
        setErr({ title: "Microsoft returned an error", body: `${errCode}: ${errDesc || "(no description)"}` });
        toast.error(`${errCode}`);
        return;
      }

      await loadEntraConfig(api);
      const { result, error: msalError } = await handleRedirectResult();
      if (msalError) {
        setErr({
          title: `MSAL failed to process redirect (${msalError.code})`,
          body: msalError.message,
        });
        return;
      }

      const code = params.get("code") || hashErr.get("code");
      const idToken = result?.idToken;

      if (!idToken && !code) {
        setErr({
          title: "No sign-in response detected",
          body: `Microsoft redirected here but didn't send a code or id_token. Check your Entra app registration — the redirect URI must be EXACTLY ${window.location.origin}/auth/entra/callback`,
        });
        return;
      }

      if (!idToken && code) {
        // MSAL parsed the response but somehow didn't get an id_token. This
        // means openid scope is missing, or MSAL bailed early. Surface it.
        setErr({
          title: "Microsoft did not return an id_token",
          body:
            "Sign-in completed but MSAL could not extract an id_token from the response. " +
            "In Azure Portal → App Registrations → your app → API Permissions, confirm that " +
            "openid, profile and email (Microsoft Graph, Delegated) are granted and that admin " +
            "consent has been given. Then try again in a fresh incognito window.",
        });
        return;
      }

      try {
        await api.post("/auth/entra/callback", {
          id_token: idToken,
          code: idToken ? undefined : code,
          redirect_uri: `${window.location.origin}/auth/entra/callback`,
        });
        await refresh();
        toast.success("Signed in with your work Microsoft account");
        navigate("/profile", { replace: true });
      } catch (e) {
        // FastAPI 422s return `detail` as an ARRAY of validation errors; stringify safely.
        let detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message ?? "Entra sign-in failed";
        if (typeof detail !== "string") {
          try { detail = JSON.stringify(detail, null, 2); } catch (_) { detail = String(detail); }
        }
        const status = e?.response?.status;
        setErr({ title: `Backend rejected sign-in${status ? ` (HTTP ${status})` : ""}`, body: detail });
      }
    })();
  }, []);

  // Iframe path: render nothing so silent-SSO doesn't spin up the whole app.
  if (inIframe) return null;

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6">
        <div className="max-w-lg w-full bg-zinc-900 border border-zinc-800 rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-zinc-100">{err.title}</div>
              <div className="text-xs text-zinc-400 mt-2 font-mono whitespace-pre-wrap break-words">{err.body}</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="w-full h-10 rounded-md bg-white text-zinc-950 hover:bg-zinc-200 text-sm font-medium"
            data-testid="entra-error-back"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950">
      <div className="flex items-center gap-3 text-zinc-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span data-testid="entra-callback-msg">{msg}</span>
      </div>
    </div>
  );
}
